import { useEffect, useMemo, useRef, useState } from "react";
import { GraphLab } from "./components/GraphLab";
import { getLecture, getPartLectures, glossary, lectures, parts } from "./data/course";
import { readStorage, writeStorage } from "./storage";
import type { Lecture } from "./types";

type Route =
  | { page: "home" }
  | { page: "lecture"; number: number }
  | { page: "lab" }
  | { page: "search" }
  | { page: "glossary" }
  | { page: "bookmarks" }
  | { page: "about" };

function parseRoute(): Route {
  const hash = window.location.hash.replace(/^#\/?/, "");
  if (hash.startsWith("lecture/")) {
    const number = Number(hash.split("/")[1]);
    return { page: "lecture", number: Number.isFinite(number) && number >= 1 && number <= 34 ? number : 1 };
  }
  if (["lab", "search", "glossary", "bookmarks", "about"].includes(hash)) return { page: hash as Route["page"] } as Route;
  return { page: "home" };
}

function go(path = "") {
  window.location.hash = path ? `/${path}` : "/";
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function partRoman(number: number) {
  return ["I", "II", "III", "IV", "V"][number - 1];
}

function courseProgress(completed: number[]) {
  return Math.round((completed.length / lectures.length) * 100);
}

function Mark({ children }: { children: React.ReactNode }) {
  return <span className="pill">{children}</span>;
}

function Sidebar({ route, completed, mobileOpen, close }: { route: Route; completed: number[]; mobileOpen: boolean; close: () => void }) {
  return (
    <>
      {mobileOpen && <button className="nav-scrim" aria-label="Close course navigation" onClick={close} />}
      <aside className={mobileOpen ? "sidebar open" : "sidebar"} aria-label="Course navigation">
        <button className="brand side-brand" onClick={() => { go(); close(); }}>
          <span className="brand-mark"><i /><i /><i /></span>
          <span><b>Graph Theory</b><small>Networks & Consciousness</small></span>
        </button>
        <div className="side-progress">
          <div><span>Your progress</span><b>{courseProgress(completed)}%</b></div>
          <div className="progress-track"><i style={{ width: `${courseProgress(completed)}%` }} /></div>
        </div>
        <nav className="course-nav">
          {parts.map((part) => (
            <section key={part.number}>
              <div className="part-label">
                <span style={{ background: part.color }}>{partRoman(part.number)}</span>
                <div><small>Part {partRoman(part.number)}</small><b>{part.title}</b></div>
              </div>
              <div className="lecture-links">
                {getPartLectures(part.number).map((lecture) => (
                  <button
                    key={lecture.number}
                    className={route.page === "lecture" && route.number === lecture.number ? "active" : ""}
                    onClick={() => { go(`lecture/${lecture.number}`); close(); }}
                  >
                    <i className={completed.includes(lecture.number) ? "done" : ""}>{completed.includes(lecture.number) ? "✓" : lecture.number}</i>
                    <span>{lecture.title}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </nav>
        <button className="side-lab" onClick={() => { go("lab"); close(); }}>
          <span>⌁</span><div><b>Graph laboratory</b><small>Draw, connect, discover</small></div><i>→</i>
        </button>
      </aside>
    </>
  );
}

function Header({
  theme,
  setTheme,
  textScale,
  setTextScale,
  openMenu,
}: {
  theme: string;
  setTheme: (value: string) => void;
  textScale: number;
  setTextScale: (value: number) => void;
  openMenu: () => void;
}) {
  return (
    <header className="topbar">
      <button className="menu-button" onClick={openMenu} aria-label="Open course navigation">☰</button>
      <button className="mobile-brand" onClick={() => go()}>
        <span className="brand-mark"><i /><i /><i /></span>
        <b>Graph Theory</b>
      </button>
      <div className="top-actions">
        <button className="search-button" onClick={() => go("search")} aria-label="Search the course">
          <span>⌕</span><b>Search the course</b><kbd>/</kbd>
        </button>
        <button className="icon-button" onClick={() => go("bookmarks")} aria-label="View bookmarks">◇</button>
        <div className="text-size" aria-label="Text size">
          <button disabled={textScale <= 0.9} onClick={() => setTextScale(Math.max(0.9, textScale - 0.1))}>A</button>
          <button disabled={textScale >= 1.2} onClick={() => setTextScale(Math.min(1.2, textScale + 0.1))}>A+</button>
        </div>
        <button className="icon-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label={`Use ${theme === "dark" ? "light" : "dark"} theme`}>
          {theme === "dark" ? "☀" : "☾"}
        </button>
      </div>
    </header>
  );
}

function HeroGraph() {
  return (
    <div className="hero-graph" aria-label="A visual journey from Euler through networks to consciousness">
      <div className="orbit one" />
      <div className="orbit two" />
      <div className="node n1"><b>E</b><small>1736</small></div>
      <div className="node n2"><b>G</b><small>network</small></div>
      <div className="node n3"><b>Φ</b><small>integration</small></div>
      <div className="node n4"><b>AI</b><small>embodied</small></div>
      <i className="edge e1" /><i className="edge e2" /><i className="edge e3" /><i className="edge e4" />
      <div className="hero-graph-caption"><span>Objects</span><span>Relations</span><span>Systems</span><span>Experience?</span></div>
    </div>
  );
}

function Home({ completed }: { completed: number[] }) {
  const next = lectures.find((lecture) => !completed.includes(lecture.number)) ?? lectures[0];
  return (
    <main>
      <section className="hero">
        <div className="hero-copy">
          <span className="eyebrow">An interactive course in 34 lectures</span>
          <h1>Graph Theory,<br /><em>Networks, and</em><br />Consciousness</h1>
          <p className="hero-deck">From Euler’s bridges to embodied artificial intelligence—a rigorous, visual journey through the mathematics of relationships.</p>
          <div className="hero-actions">
            <button className="primary-button" onClick={() => go(`lecture/${next.number}`)}>
              {completed.length ? "Continue your course" : "Begin Lecture 1"} <span>→</span>
            </button>
            <button className="secondary-button" onClick={() => go("lab")}>Open graph laboratory</button>
          </div>
          <div className="hero-facts">
            <div><strong>5</strong><span>connected parts</span></div>
            <div><strong>34</strong><span>guided lectures</span></div>
            <div><strong>∞</strong><span>possible graphs</span></div>
          </div>
        </div>
        <HeroGraph />
      </section>

      <section className="continue-band">
        <div className="continue-copy">
          <span className="eyebrow">Your next step</span>
          <h2>Lecture {next.number}: {next.title}</h2>
          <p>{next.summary}</p>
        </div>
        <div className="continue-meta">
          <div className="ring" style={{ "--value": `${courseProgress(completed) * 3.6}deg` } as React.CSSProperties}>
            <span>{courseProgress(completed)}<small>%</small></span>
          </div>
          <div><b>{completed.length} of 34 complete</b><span>{next.minutes} min · Part {partRoman(next.part)}</span></div>
        </div>
        <button className="round-button" onClick={() => go(`lecture/${next.number}`)} aria-label={`Open lecture ${next.number}`}>→</button>
      </section>

      <section className="course-map page-section">
        <div className="section-heading">
          <div><span className="eyebrow">Course map</span><h2>One language, five scales</h2></div>
          <p>Each part adds a new way to see—from the grammar of connection to the unsettled question of artificial experience.</p>
        </div>
        <div className="part-cards">
          {parts.map((part) => {
            const partLectures = getPartLectures(part.number);
            const complete = partLectures.filter((lecture) => completed.includes(lecture.number)).length;
            return (
              <article className="part-card card" key={part.number} style={{ "--part": part.color } as React.CSSProperties}>
                <div className="part-card-top"><span>{partRoman(part.number)}</span><i>{String(partLectures.length).padStart(2, "0")} lectures</i></div>
                <small>{part.kicker}</small>
                <h3>{part.title}</h3>
                <p>{part.description}</p>
                <div className="part-progress"><i style={{ width: `${(complete / partLectures.length) * 100}%` }} /></div>
                <button onClick={() => go(`lecture/${partLectures[0].number}`)}>
                  {complete ? `${complete}/${partLectures.length} complete` : "Explore this part"} <span>↗</span>
                </button>
              </article>
            );
          })}
        </div>
      </section>

      <section className="modes-section page-section">
        <div className="section-heading">
          <div><span className="eyebrow">Learn in your rhythm</span><h2>Four ways into every idea</h2></div>
        </div>
        <div className="mode-grid">
          <article><span>01</span><h3>Read</h3><p>Clear, substantial lectures with objectives, examples, and reflection.</p></article>
          <article><span>02</span><h3>Listen</h3><p>Browser narration and a focused commute view for learning away from a desk.</p></article>
          <article><span>03</span><h3>Practise</h3><p>Immediate quiz feedback, worked examples, and notes that remain on your device.</p></article>
          <article><span>04</span><h3>Experiment</h3><p>A live graph laboratory makes degree, connectivity, and Eulerian structure tangible.</p></article>
        </div>
      </section>

      <section className="quote-section page-section">
        <span className="giant-quote">“</span>
        <blockquote>Many problems depend more on relationships than on the objects themselves.</blockquote>
        <p>The guiding idea of this course</p>
      </section>
    </main>
  );
}

function useSpeech(lecture: Lecture) {
  const [status, setStatus] = useState<"idle" | "playing" | "paused">("idle");
  const [rate, setRate] = useState(1);
  const utterance = useRef<SpeechSynthesisUtterance | null>(null);
  const supported = "speechSynthesis" in window;
  const text = useMemo(() => [
    `Lecture ${lecture.number}. ${lecture.title}. ${lecture.subtitle}.`,
    lecture.summary,
    ...lecture.objectives,
    ...lecture.sections.flatMap((section) => [section.heading, ...section.paragraphs]),
    lecture.workedExample.title,
    lecture.workedExample.setup,
    ...lecture.workedExample.steps,
    lecture.workedExample.conclusion,
    `Reflection. ${lecture.reflection}`,
  ].join(" "), [lecture]);

  useEffect(() => () => {
    if (supported) window.speechSynthesis.cancel();
  }, [lecture.number, supported]);

  function play() {
    if (!supported) return;
    if (status === "paused") {
      window.speechSynthesis.resume();
      setStatus("playing");
      return;
    }
    window.speechSynthesis.cancel();
    const next = new SpeechSynthesisUtterance(text);
    next.rate = rate;
    next.pitch = 1;
    next.onend = () => setStatus("idle");
    next.onerror = () => setStatus("idle");
    utterance.current = next;
    window.speechSynthesis.speak(next);
    setStatus("playing");
  }

  function pause() {
    if (!supported) return;
    window.speechSynthesis.pause();
    setStatus("paused");
  }

  function stop() {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setStatus("idle");
  }

  function changeRate(value: number) {
    setRate(value);
    if (status !== "idle") {
      stop();
      setTimeout(play, 0);
    }
  }
  return { status, rate, supported, play, pause, stop, changeRate };
}

function Quiz({ lecture }: { lecture: Lecture }) {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => { setAnswers({}); setSubmitted(false); }, [lecture.number]);
  const score = lecture.quiz.filter((question, index) => answers[index] === question.answer).length;
  return (
    <section className="quiz card" id="quiz">
      <div className="quiz-head">
        <div><span className="eyebrow">Check your understanding</span><h2>Lecture quiz</h2></div>
        <span>{lecture.quiz.length} {lecture.quiz.length === 1 ? "question" : "questions"}</span>
      </div>
      {lecture.quiz.map((question, index) => (
        <article key={question.prompt} className="quiz-question">
          <h3><span>{String(index + 1).padStart(2, "0")}</span>{question.prompt}</h3>
          <div className="choices">
            {question.choices.map((choice, choiceIndex) => {
              let className = answers[index] === choiceIndex ? "selected" : "";
              if (submitted && choiceIndex === question.answer) className = "correct";
              if (submitted && answers[index] === choiceIndex && choiceIndex !== question.answer) className = "wrong";
              return <button key={choice} className={className} disabled={submitted} onClick={() => setAnswers((current) => ({ ...current, [index]: choiceIndex }))}><i>{String.fromCharCode(65 + choiceIndex)}</i>{choice}</button>;
            })}
          </div>
          {submitted && <p className="quiz-explanation">{answers[index] === question.answer ? "Correct. " : "Not quite. "}{question.explanation}</p>}
        </article>
      ))}
      <div className="quiz-footer">
        {submitted ? (
          <><p><strong>{score}/{lecture.quiz.length}</strong> correct</p><button className="secondary-button" onClick={() => { setAnswers({}); setSubmitted(false); }}>Try again</button></>
        ) : (
          <button className="primary-button" disabled={Object.keys(answers).length < lecture.quiz.length} onClick={() => setSubmitted(true)}>Check answers</button>
        )}
      </div>
    </section>
  );
}

function LecturePage({
  lecture,
  completed,
  bookmarks,
  notes,
  setNotes,
  toggleComplete,
  toggleBookmark,
}: {
  lecture: Lecture;
  completed: number[];
  bookmarks: number[];
  notes: Record<number, string>;
  setNotes: (value: Record<number, string>) => void;
  toggleComplete: (number: number) => void;
  toggleBookmark: (number: number) => void;
}) {
  const [commute, setCommute] = useState(false);
  const speech = useSpeech(lecture);
  const part = parts[lecture.part - 1];
  const previous = lecture.number > 1 ? getLecture(lecture.number - 1) : null;
  const next = lecture.number < lectures.length ? getLecture(lecture.number + 1) : null;
  useEffect(() => setCommute(false), [lecture.number]);

  return (
    <main className={commute ? "lecture-page commute" : "lecture-page"}>
      <div className="lecture-progress-line" style={{ width: `${(lecture.number / lectures.length) * 100}%`, background: part.color }} />
      <section className="lecture-hero" style={{ "--part": part.color } as React.CSSProperties}>
        <div className="lecture-breadcrumb">Part {partRoman(part.number)} <span>·</span> {part.title}</div>
        <div className="lecture-index">{String(lecture.number).padStart(2, "0")}</div>
        <h1>{lecture.title}</h1>
        <p className="lecture-subtitle">{lecture.subtitle}</p>
        <div className="lecture-tags">
          <Mark>{lecture.minutes} min read</Mark>
          <Mark>{lecture.keyTerms.length} key ideas</Mark>
          <Mark>Lecture {lecture.number} of 34</Mark>
        </div>
        <div className="lecture-actions">
          <button className={bookmarks.includes(lecture.number) ? "action-button active" : "action-button"} onClick={() => toggleBookmark(lecture.number)}>
            {bookmarks.includes(lecture.number) ? "◆ Bookmarked" : "◇ Bookmark"}
          </button>
          <button className="action-button" onClick={() => setCommute(!commute)}>{commute ? "Exit commute mode" : "◉ Commute mode"}</button>
        </div>
      </section>

      <section className="listen-bar">
        <button className="listen-main" disabled={!speech.supported} onClick={speech.status === "playing" ? speech.pause : speech.play}>
          <span>{speech.status === "playing" ? "Ⅱ" : "▶"}</span>
          <div><b>{speech.status === "playing" ? "Pause lecture" : speech.status === "paused" ? "Resume lecture" : "Listen to this lecture"}</b><small>Browser narration · approximately {Math.ceil(lecture.minutes * 0.8)} minutes</small></div>
        </button>
        {speech.status !== "idle" && <button className="stop-button" onClick={speech.stop}>Stop</button>}
        <label>Speed
          <select value={speech.rate} onChange={(event) => speech.changeRate(Number(event.target.value))}>
            <option value={0.85}>0.85×</option><option value={1}>1×</option><option value={1.15}>1.15×</option><option value={1.35}>1.35×</option>
          </select>
        </label>
      </section>

      <div className="lecture-layout">
        <article className="lecture-content">
          <p className="standfirst">{lecture.summary}</p>
          <section className="objectives">
            <span className="eyebrow">By the end of this lecture</span>
            <ol>{lecture.objectives.map((objective) => <li key={objective}>{objective}</li>)}</ol>
          </section>
          {lecture.sections.map((section, index) => (
            <section className="prose-section" key={section.heading} id={`section-${index}`}>
              <span className="section-number">{String(index + 1).padStart(2, "0")}</span>
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </section>
          ))}
          <section className="worked-example">
            <div className="example-label"><span>Worked example</span><i>Think on paper</i></div>
            <h2>{lecture.workedExample.title}</h2>
            <p>{lecture.workedExample.setup}</p>
            <ol>{lecture.workedExample.steps.map((step) => <li key={step}>{step}</li>)}</ol>
            <p className="example-conclusion"><b>Conclusion.</b> {lecture.workedExample.conclusion}</p>
          </section>
          <section className="reflection">
            <span>Pause & consider</span>
            <blockquote>{lecture.reflection}</blockquote>
          </section>
          <Quiz lecture={lecture} />
          <section className="notes card">
            <div><span className="eyebrow">Private notes</span><small>Saved only on this device</small></div>
            <textarea
              value={notes[lecture.number] ?? ""}
              onChange={(event) => setNotes({ ...notes, [lecture.number]: event.target.value })}
              placeholder="What surprised you? What should you revisit?"
              aria-label={`Notes for lecture ${lecture.number}`}
            />
          </section>
          <div className="completion-row">
            <button className={completed.includes(lecture.number) ? "complete-button done" : "complete-button"} onClick={() => toggleComplete(lecture.number)}>
              {completed.includes(lecture.number) ? "✓ Lecture complete" : "Mark lecture complete"}
            </button>
          </div>
        </article>
        <aside className="lecture-aside">
          <nav className="on-page card">
            <span className="eyebrow">In this lecture</span>
            <a href="#section-0">01 The central idea</a>
            <a href="#section-1">02 Why it matters</a>
            <a href="#quiz">03 Check understanding</a>
          </nav>
          <div className="key-terms card">
            <span className="eyebrow">Key terms</span>
            {lecture.keyTerms.map((term) => <button key={term} onClick={() => go("glossary")}>{term}<span>↗</span></button>)}
          </div>
          <button className="mini-lab card" onClick={() => go("lab")}>
            <span>⌁</span><b>Try it in the laboratory</b><small>Change a graph and inspect its structure.</small>
          </button>
        </aside>
      </div>
      <nav className="lecture-next">
        {previous ? <button onClick={() => go(`lecture/${previous.number}`)}><span>← Previous</span><b>{previous.title}</b></button> : <i />}
        {next ? <button className="next" onClick={() => go(`lecture/${next.number}`)}><span>Next →</span><b>{next.title}</b></button> : <button className="next" onClick={() => go()}><span>Course complete</span><b>Return home</b></button>}
      </nav>
    </main>
  );
}

function SearchPage({ bookmarks }: { bookmarks: number[] }) {
  const [query, setQuery] = useState("");
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => input.current?.focus(), []);
  const results = useMemo(() => {
    const clean = query.trim().toLowerCase();
    if (!clean) return lectures.slice(0, 8);
    return lectures.filter((lecture) => [
      lecture.title, lecture.subtitle, lecture.summary, ...lecture.keyTerms,
      ...lecture.sections.flatMap((section) => [section.heading, ...section.paragraphs]),
    ].join(" ").toLowerCase().includes(clean));
  }, [query]);
  return (
    <main className="utility-page">
      <header className="utility-head">
        <span className="eyebrow">Course index</span><h1>Search all 34 lectures</h1>
        <div className="search-field"><span>⌕</span><input ref={input} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Try “Laplacian”, “consciousness”, or “shortest path”" /></div>
      </header>
      <section className="result-list">
        <p>{query ? `${results.length} result${results.length === 1 ? "" : "s"}` : "Suggested starting points"}</p>
        {results.map((lecture) => (
          <button key={lecture.number} onClick={() => go(`lecture/${lecture.number}`)}>
            <span className="result-number">{String(lecture.number).padStart(2, "0")}</span>
            <div><small>Part {partRoman(lecture.part)} · {parts[lecture.part - 1].title}</small><h2>{lecture.title}</h2><p>{lecture.summary}</p><div>{lecture.keyTerms.slice(0, 3).map((term) => <Mark key={term}>{term}</Mark>)}</div></div>
            <i>{bookmarks.includes(lecture.number) ? "◆" : "→"}</i>
          </button>
        ))}
        {!results.length && <div className="empty-state"><span>∅</span><h2>No lecture found</h2><p>Try a broader idea or visit the glossary for exact terms.</p></div>}
      </section>
    </main>
  );
}

function GlossaryPage() {
  const [query, setQuery] = useState("");
  const filtered = glossary.filter((entry) => `${entry.term} ${entry.definition}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <main className="utility-page">
      <header className="utility-head">
        <span className="eyebrow">Reference</span><h1>Glossary</h1>
        <p>Precise language for a field built on simple objects and subtle distinctions.</p>
        <div className="search-field small"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter terms" /></div>
      </header>
      <section className="glossary-grid">
        {filtered.map((entry) => (
          <article className="card" key={entry.term}>
            <h2>{entry.term}</h2><p>{entry.definition}</p>
            <button onClick={() => go(`lecture/${entry.lecture}`)}>Lecture {entry.lecture} <span>↗</span></button>
          </article>
        ))}
      </section>
    </main>
  );
}

function BookmarksPage({ bookmarks, notes }: { bookmarks: number[]; notes: Record<number, string> }) {
  const saved = lectures.filter((lecture) => bookmarks.includes(lecture.number));
  return (
    <main className="utility-page">
      <header className="utility-head"><span className="eyebrow">Your library</span><h1>Bookmarks</h1><p>Return to the ideas you want to carry forward.</p></header>
      {saved.length ? <section className="result-list">
        {saved.map((lecture) => <button key={lecture.number} onClick={() => go(`lecture/${lecture.number}`)}><span className="result-number">◆</span><div><small>Lecture {lecture.number}</small><h2>{lecture.title}</h2><p>{notes[lecture.number] || lecture.summary}</p></div><i>→</i></button>)}
      </section> : <section className="empty-state tall"><span>◇</span><h2>Your bookmarks are waiting</h2><p>Use the bookmark button on any lecture to build a personal reading list.</p><button className="primary-button" onClick={() => go("lecture/1")}>Browse Lecture 1</button></section>}
    </main>
  );
}

function LabPage() {
  return (
    <main className="lab-page utility-page">
      <header className="utility-head"><span className="eyebrow">Laboratory mode</span><h1>Make the structure move</h1><p>Add, connect, drag, and remove vertices. The analysis updates as you work.</p></header>
      <GraphLab />
      <section className="lab-guide">
        <article><span>01</span><h3>Count degrees</h3><p>Every new edge raises the degree of both endpoints by one.</p></article>
        <article><span>02</span><h3>Break connectivity</h3><p>Delete a bridge vertex and watch the component count change.</p></article>
        <article><span>03</span><h3>Make Euler happy</h3><p>Adjust edges until the graph has zero or exactly two odd-degree vertices.</p></article>
      </section>
    </main>
  );
}

function AboutPage() {
  return (
    <main className="utility-page about-page">
      <header className="utility-head"><span className="eyebrow">About the course</span><h1>From a city puzzle to the boundaries of mind</h1><p>This course treats graph theory as both rigorous mathematics and a shared language for complex systems.</p></header>
      <section className="about-grid">
        <article className="card"><span>1736</span><h2>Begin with Euler</h2><p>Learn why preserving connection while discarding irrelevant detail became one of mathematics’ most powerful habits.</p></article>
        <article className="card"><span>34 lectures</span><h2>Build the toolkit</h2><p>Move through algorithms, network science, spectra, higher-order structure, neural systems, and artificial intelligence.</p></article>
        <article className="card"><span>Open question</span><h2>End with discipline</h2><p>Use graphs to make theories of consciousness more precise without claiming that structure alone resolves the mystery.</p></article>
      </section>
    </main>
  );
}

function Footer() {
  return <footer className="site-footer"><div><b>Graph Theory, Networks, and Consciousness</b><span>From Euler to Embodied Artificial Intelligence</span></div><nav><button onClick={() => go("about")}>About</button><button onClick={() => go("glossary")}>Glossary</button><button onClick={() => go("lab")}>Laboratory</button></nav><small>Progress and notes remain private in your browser.</small></footer>;
}

export default function App() {
  const [route, setRoute] = useState<Route>(parseRoute);
  const [completed, setCompleted] = useState<number[]>(() => readStorage("completed", []));
  const [bookmarks, setBookmarks] = useState<number[]>(() => readStorage("bookmarks", []));
  const [notes, setNotesState] = useState<Record<number, string>>(() => readStorage("notes", {}));
  const [theme, setThemeState] = useState(() => readStorage("theme", window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"));
  const [textScale, setTextScaleState] = useState<number>(() => readStorage("text-scale", 1));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const change = () => setRoute(parseRoute());
    window.addEventListener("hashchange", change);
    if (!window.location.hash) window.history.replaceState(null, "", "#/");
    return () => window.removeEventListener("hashchange", change);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.setProperty("--text-scale", String(textScale));
  }, [theme, textScale]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "/" && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
        event.preventDefault();
        go("search");
      }
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    if ("serviceWorker" in navigator && import.meta.env.PROD) navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  }, []);

  function setTheme(value: string) { setThemeState(value); writeStorage("theme", value); }
  function setTextScale(value: number) { const rounded = Math.round(value * 10) / 10; setTextScaleState(rounded); writeStorage("text-scale", rounded); }
  function setNotes(value: Record<number, string>) { setNotesState(value); writeStorage("notes", value); }
  function toggleList(value: number, list: number[], setter: (value: number[]) => void, key: string) {
    const next = list.includes(value) ? list.filter((item) => item !== value) : [...list, value].sort((a, b) => a - b);
    setter(next); writeStorage(key, next);
  }

  let page: React.ReactNode;
  if (route.page === "lecture") page = <LecturePage lecture={getLecture(route.number)} completed={completed} bookmarks={bookmarks} notes={notes} setNotes={setNotes} toggleComplete={(number) => toggleList(number, completed, setCompleted, "completed")} toggleBookmark={(number) => toggleList(number, bookmarks, setBookmarks, "bookmarks")} />;
  else if (route.page === "lab") page = <LabPage />;
  else if (route.page === "search") page = <SearchPage bookmarks={bookmarks} />;
  else if (route.page === "glossary") page = <GlossaryPage />;
  else if (route.page === "bookmarks") page = <BookmarksPage bookmarks={bookmarks} notes={notes} />;
  else if (route.page === "about") page = <AboutPage />;
  else page = <Home completed={completed} />;

  return (
    <div className="app-shell">
      <Sidebar route={route} completed={completed} mobileOpen={mobileOpen} close={() => setMobileOpen(false)} />
      <div className="main-shell">
        <Header theme={theme} setTheme={setTheme} textScale={textScale} setTextScale={setTextScale} openMenu={() => setMobileOpen(true)} />
        {page}
        <Footer />
      </div>
    </div>
  );
}
